/** XeAgents.ai**/
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { cn } from "@/lib/utils";

/** Body of `POST /api/auth/login` when the server wants an emailed code before issuing a token. */
export interface LoginMfaChallenge {
  mfaRequired: true;
  challengeId: string;
  /** Already masked by the server, e.g. "a***@x.com". */
  email: string;
  codeLength: number;
  expiresInSeconds: number;
  resendAfterSeconds: number;
}

/** Success payload of `POST /api/auth/login/verify-code` - identical to a normal login success. */
export interface LoginSuccessData {
  user: any;
  token: string;
  mustChangePassword?: boolean;
}

export function isMfaChallenge(data: any): data is LoginMfaChallenge {
  return !!data && data.mfaRequired === true && typeof data.challengeId === "string" && data.challengeId.length > 0;
}

interface LoginMfaStepProps {
  challenge: LoginMfaChallenge;
  /** Called with the verify-code success payload; the caller runs its normal post-login handling. */
  onSuccess: (data: LoginSuccessData) => void;
  /** Return to the password form. `message` (when present) explains why and should be shown to the user. */
  onBack: (message?: string) => void;
  /** Extra classes for the primary "verify" button so it can match the host page's button style. */
  submitButtonClassName?: string;
}

const DEFAULT_CODE_LENGTH = 6;

function toSeconds(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.ceil(n) : fallback;
}

function secondsLeft(deadline: number, now: number): number {
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

export function LoginMfaStep({ challenge, onSuccess, onBack, submitButtonClassName }: LoginMfaStepProps) {
  const { t } = useTranslation();

  const codeLength =
    Number.isInteger(challenge.codeLength) && challenge.codeLength > 0 ? challenge.codeLength : DEFAULT_CODE_LENGTH;
  // Isolate the address so it renders correctly inside right-to-left text (Arabic).
  const maskedEmail = `⁨${challenge.email}⁩`;

  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [resendExhausted, setResendExhausted] = useState(false);

  // Countdowns are deadline based so they stay accurate when the tab is throttled in the background.
  const [now, setNow] = useState(() => Date.now());
  const [resendAt, setResendAt] = useState(() => Date.now() + toSeconds(challenge.resendAfterSeconds, 0) * 1000);
  const [expiresAt, setExpiresAt] = useState(() => Date.now() + toSeconds(challenge.expiresInSeconds, 0) * 1000);

  const inputRef = useRef<HTMLInputElement>(null);
  const submittingRef = useRef(false);

  // Always call the latest callbacks without re-creating handlers.
  const onSuccessRef = useRef(onSuccess);
  const onBackRef = useRef(onBack);
  onSuccessRef.current = onSuccess;
  onBackRef.current = onBack;

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Focus the code input on mount and again whenever a request finishes (a disabled input loses focus).
  useEffect(() => {
    if (!isVerifying && !isResending) {
      inputRef.current?.focus();
    }
  }, [isVerifying, isResending]);

  const resendSecondsLeft = secondsLeft(resendAt, now);
  const expiresSecondsLeft = secondsLeft(expiresAt, now);
  const expiryKnown = toSeconds(challenge.expiresInSeconds, 0) > 0;

  const startResendCooldown = (seconds: number) => setResendAt(Date.now() + seconds * 1000);
  const startExpiry = (seconds: number) => setExpiresAt(Date.now() + seconds * 1000);

  const errorText = (data: any, fallback: string) =>
    typeof data?.error === "string" && data.error ? data.error : fallback;

  const verify = async (value: string) => {
    if (submittingRef.current || value.length !== codeLength) return;
    submittingRef.current = true;
    setIsVerifying(true);
    setError(null);
    setNotice(null);
    setAttemptsRemaining(null);

    let handedOff = false;
    try {
      const response = await fetch("/api/auth/login/verify-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId: challenge.challengeId, code: value }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        // Stay in the "verifying" state: the caller redirects or swaps this step out.
        handedOff = true;
        onSuccessRef.current(data);
        return;
      }

      switch (data?.code) {
        case "MFA_CODE_INVALID":
          setError(t("auth.mfa.invalidCode"));
          if (typeof data.attemptsRemaining === "number") {
            setAttemptsRemaining(Math.max(0, data.attemptsRemaining));
          }
          setCode("");
          return;
        case "MFA_CODE_EXPIRED":
          onBackRef.current(t("auth.mfa.errorExpired"));
          return;
        case "MFA_LOCKED":
          onBackRef.current(t("auth.mfa.errorLocked"));
          return;
        case "MFA_CHALLENGE_INVALID":
          onBackRef.current(t("auth.mfa.errorChallengeInvalid"));
          return;
        case "ACCOUNT_LOCKED":
        case "ACCOUNT_SUSPENDED":
          onBackRef.current(errorText(data, t("auth.mfa.errorChallengeInvalid")));
          return;
      }

      if (response.status === 423 || response.status === 403) {
        onBackRef.current(errorText(data, t("auth.mfa.errorChallengeInvalid")));
        return;
      }

      // 429 and anything unexpected: show the server message and let the user retry.
      setError(errorText(data, t("auth.mfa.verifyFailed")));
    } catch {
      setError(t("auth.mfa.networkError"));
    } finally {
      if (!handedOff) {
        submittingRef.current = false;
        setIsVerifying(false);
      }
    }
  };

  const handleResend = async () => {
    if (isResending || isVerifying || resendExhausted || resendSecondsLeft > 0) return;
    setIsResending(true);
    setError(null);
    setNotice(null);

    try {
      const response = await fetch("/api/auth/login/resend-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId: challenge.challengeId }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        startResendCooldown(toSeconds(data.resendAfterSeconds, toSeconds(challenge.resendAfterSeconds, 0)));
        if (data.expiresInSeconds !== undefined) {
          startExpiry(toSeconds(data.expiresInSeconds, 0));
        }
        setCode("");
        setAttemptsRemaining(null);
        setNotice(t("auth.mfa.resent", { email: maskedEmail }));
        return;
      }

      switch (data?.code) {
        case "MFA_RESEND_COOLDOWN":
          startResendCooldown(toSeconds(data.retryAfterSeconds, toSeconds(challenge.resendAfterSeconds, 0)));
          return;
        case "MFA_RESEND_LIMIT":
          // The last code that was sent may still be valid, so stay here but stop offering resends.
          setResendExhausted(true);
          setError(t("auth.mfa.resendLimit"));
          return;
        case "MFA_CHALLENGE_INVALID":
          onBackRef.current(t("auth.mfa.errorChallengeInvalid"));
          return;
        case "MFA_CODE_EXPIRED":
          onBackRef.current(t("auth.mfa.errorExpired"));
          return;
      }

      // 503 MFA_DELIVERY_FAILED, generic 429 and anything unexpected: show it and stay on the code step.
      setError(errorText(data, t("auth.mfa.resendFailed")));
    } catch {
      setError(t("auth.mfa.networkError"));
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    verify(code);
  };

  const busy = isVerifying || isResending;

  let resendLabel = t("auth.mfa.resend");
  if (isResending) {
    resendLabel = t("auth.mfa.resending");
  } else if (resendSecondsLeft > 0) {
    resendLabel = t("auth.mfa.resendIn", { seconds: resendSecondsLeft });
  }

  const expiryMinutes = Math.floor(expiresSecondsLeft / 60);
  const expirySeconds = String(expiresSecondsLeft % 60).padStart(2, "0");

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="form-mfa">
      <div className="text-center space-y-2">
        <div className="flex justify-center mb-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Mail className="h-6 w-6 text-primary" aria-hidden="true" />
          </div>
        </div>
        <h3 className="font-semibold text-lg">{t("auth.mfa.title")}</h3>
        <p className="text-sm text-muted-foreground" data-testid="text-mfa-description">
          {t("auth.mfa.description", { length: codeLength, email: maskedEmail })}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="login-mfa-code" className="text-center block">
          {t("auth.mfa.codeLabel")}
        </Label>
        <div className="flex justify-center" dir="ltr">
          <InputOTP
            id="login-mfa-code"
            ref={inputRef}
            maxLength={codeLength}
            value={code}
            onChange={setCode}
            onComplete={(value: string) => verify(value)}
            disabled={isVerifying}
            pattern={REGEXP_ONLY_DIGITS}
            pasteTransformer={(pasted) => pasted.replace(/\D/g, "")}
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-label={t("auth.mfa.codeAriaLabel", { length: codeLength })}
            aria-invalid={error ? true : undefined}
            data-testid="input-mfa-code"
          >
            <InputOTPGroup>
              {Array.from({ length: codeLength }, (_, i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>
      </div>

      <div className="space-y-1 text-center text-sm">
        {error && (
          <p role="alert" className="text-destructive" data-testid="text-mfa-error">
            {error}
          </p>
        )}
        {attemptsRemaining !== null && (
          <p className="text-muted-foreground" data-testid="text-mfa-attempts">
            {t("auth.mfa.attemptsRemaining", { attempts: attemptsRemaining })}
          </p>
        )}
        {notice && (
          <p role="status" className="text-muted-foreground" data-testid="text-mfa-notice">
            {notice}
          </p>
        )}
        {expiryKnown && (
          <p className="text-muted-foreground" data-testid="text-mfa-expiry">
            {expiresSecondsLeft > 0
              ? t("auth.mfa.expiresIn", { time: `${expiryMinutes}:${expirySeconds}` })
              : t("auth.mfa.expiredHint")}
          </p>
        )}
      </div>

      <div className="space-y-3">
        <Button
          type="submit"
          className={cn("w-full", submitButtonClassName)}
          disabled={isVerifying || isResending || code.length !== codeLength}
          data-testid="button-mfa-verify"
        >
          {isVerifying ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              {t("auth.mfa.verifying")}
            </>
          ) : (
            t("auth.mfa.verify")
          )}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleResend}
          disabled={busy || resendExhausted || resendSecondsLeft > 0}
          data-testid="button-mfa-resend"
        >
          {resendLabel}
        </Button>

        <Button
          type="button"
          variant="ghost"
          className="w-full"
          onClick={() => onBackRef.current()}
          disabled={isVerifying}
          data-testid="button-mfa-back"
        >
          {t("auth.mfa.backToSignIn")}
        </Button>
      </div>
    </form>
  );
}

export default LoginMfaStep;
